// src/components/PageForm.jsx
import React, { useEffect, useRef, useState } from "react";
import { Controller, useForm, useWatch } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { FileVolume, FileX, Landmark } from "lucide-react";
import { toast } from "react-toastify";
import { useMutation } from "@tanstack/react-query";

export default function PageForm({
  mode,
  defaultValues = {},
  onSubmit,
  onCancel,
  submissionLoading = false,
  fileUpload,
  fileDelete,
}) {
  const {
    register,
    handleSubmit,
    setValue,
    reset,
    formState: { errors },
    control,
  } = useForm({
    // Add resolver back when you want validation:
    // resolver: yupResolver(pageSchema),
    defaultValues: {
      title: defaultValues.title || "",
      sound: defaultValues.sound || "",
      is_exclusive:
        defaultValues.is_exclusive === true ||
        defaultValues.is_exclusive === "true"
          ? "true"
          : "false",
      mode,
    },
  });

  const fileInputRef = useRef(null);

  useEffect(() => {
    reset({
      title: defaultValues.title || "",
      sound: defaultValues.sound || "",
      is_exclusive:
        defaultValues.is_exclusive === true ||
        defaultValues.is_exclusive === "true"
          ? "true"
          : "false",
      mode,
    });
  }, [defaultValues, mode, reset]);

  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState(null);
  const soundValue = useWatch({ control, name: "sound" });

  const fileDeleteMutation = useMutation({
    mutationFn: async (url) => await fileDelete(url),
    onSuccess: (data) => {
      setValue("sound", "");
      if (fileInputRef.current) {
        fileInputRef.current.value = ""; // 🔹 reset the file input field
      }
      toast.success(data?.message || "File deleted successfully");
    },
    onError: (error) => {
      toast.error(error?.message || "Failed to delete sound");
    },
  });

  const handleFileDelete = (url) => {
    if (!url) {
      toast.error("No file to delete");
      return;
    }
    fileDeleteMutation.mutate(url);
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
      {/* Sound Title */}
      <div>
        <label className="block text-sm font-medium mb-1"> Title</label>
        <div className="relative">
          <input
            {...register("title")}
            readOnly={mode === "view"}
            placeholder="Enter title"
            className="pl-4 pr-3 py-2 border rounded w-full"
          />
        </div>
        {errors.title && (
          <p className="text-red-500 text-xs mt-1">{errors.title.message}</p>
        )}
      </div>

      <div>
        <label className="block text-sm font-medium mb-1"> Is Exclusive</label>
        <div className="relative">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <Landmark className="h-4 w-4 text-purple-800" />
          </div>
          <select
            {...register("is_exclusive")}
            disabled={mode === "view"}
            className="pl-10 pr-3 py-2 border rounded w-full"
          >
            <option value="false">Normal</option>
            <option value="true">Exclusive</option>
          </select>
        </div>
        {errors.is_exclusive && (
          <p className="text-red-500 text-xs mt-1">
            {errors.is_exclusive.message}
          </p>
        )}
      </div>

      {/* View Sound */}
      <div>
        {mode === "view" ? (
          <div
            className="flex items-center cursor-pointer text-gray-600"
            title="Open Sound File"
          >
            <label className="block text-sm font-medium mb-1"> Sound</label>
            <FileVolume className="ml-2 h-5 w-5 text-gray-600 mr-5" />
            <audio controls>
              <source src={soundValue} />
              Your browser does not support the audio element.
            </audio>
          </div>
        ) : (
          <div
            className="flex items-center cursor-pointer text-gray-600"
            title="Open Sound File"
          />
        )}
        {errors.sound && (
          <p className="text-red-500 text-xs mt-1">{errors.sound.message}</p>
        )}
      </div>

      {/* Upload / Existing file (when not view) */}
      {mode !== "view" && (
        <div>
          <label className="block text-sm font-medium mb-1">Upload File</label>

          {(defaultValues.sound || soundValue) && (
            <div
              className="flex items-center cursor-pointer text-gray-600 mt-2 mb-2"
              title="Open Sound File"
            >
              <FileVolume className="ml-2 h-5 w-5 text-gray-600 mr-5" />
              <audio controls>
                <source src={defaultValues.sound || soundValue} />
                Your browser does not support the audio element.
              </audio>

              <button
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  handleFileDelete(defaultValues.sound || soundValue);
                }}
                disabled={fileDeleteMutation.isLoading}
                className="ml-2"
                title="Delete uploaded sound"
              >
                <FileX className="h-5 w-5 text-gray-600" />
              </button>
            </div>
          )}

          <Controller
            name="sound"
            control={control}
            render={({ field }) => (
              <>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="audio/*"
                  onChange={async (e) => {
                    const file = e.target.files && e.target.files[0];
                    if (!file) return;
                    setUploading(true);
                    setUploadError(null);
                    try {
                      const formData = new FormData();
                      formData.append("document", file);
                      const response = await fileUpload(formData);
                      const uploadedUrl = response?.data?.url || "";
                      field.onChange(uploadedUrl);
                    } catch (error) {
                      setUploadError(error?.message || "File upload failed");
                    } finally {
                      setUploading(false);
                    }
                  }}
                  disabled={uploading}
                  className="border rounded w-full py-2 px-3"
                />
                {uploading && (
                  <p className="text-blue-500 text-xs mt-1">Uploading...</p>
                )}
                {uploadError && (
                  <p className="text-red-500 text-xs mt-1">{uploadError}</p>
                )}
              </>
            )}
          />
        </div>
      )}

      {/* Action Buttons */}
      <div className="flex justify-end space-x-3 pt-4 border-t">
        <button
          type="button"
          onClick={onCancel}
          className="px-4 py-2 border rounded text-gray-700"
        >
          {mode === "view" ? "Close" : "Cancel"}
        </button>
        {mode !== "view" && (
          <button
            type="submit"
            disabled={submissionLoading}
            className="px-4 py-2 bg-primary text-white rounded flex items-center justify-center gap-2"
          >
            {submissionLoading ? (
              <>
                <svg
                  className="animate-spin h-5 w-5 text-white"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8v4l3-3-3-3v4a8 8 0 100 16v-4l-3 3 3 3v-4a8 8 0 01-8-8z"
                  />
                </svg>
                <span>Loading...</span>
              </>
            ) : (
              <>{mode === "edit" ? "Save Changes" : "Create Trigger"}</>
            )}
          </button>
        )}
      </div>
    </form>
  );
}
